import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { OrangeTextButton } from "../../../../common/OrangeTextButton";
import BackdropLoadingScreen from "../../../../common/BackdropLoadingScreen";
import { useSiteAboutStore } from "../siteAboutStore/siteAboutStore";
import { ScheduleGrid } from "../ScheduleGrid";
import { addIgnoreLabel } from "../SiteAboutPageUtils/addIgnoreLabel";
import { handleConvertSchedulesToBooleanArrays } from "../SiteAboutPageUtils/handleConvertSchedulesToBooleanArrays";
import { handleCreateInitialTimeSchedules } from "../SiteAboutPageUtils/handleCreateInitialTimeSchedules";
import { handleCreateDuskToDawn } from "../SiteAboutPageUtils/handleCreateDuskToDawn";
import { handleCreateDuskToDawnLocationPlusBuffer } from "../../SitesPage/SitesPageUtils/handleCreateDuskToDawnLocationPlusBuffer";
import { useFlexIgnoreZonesOnboardingStore } from "./flexIgnoreZonesOnboardingStore";
import { FlexIgnoreZonesEditPresetCard } from "./FlexIgnoreZonesEditPresetCard";
import { FlexIgnoreZonesNewPresetDialog } from "./FlexIgnoreZonesNewPresetDialog";
import { collapseNoIgnoreZonesPresetOptions } from "./flexPresetDisplayDedupe";
import { SummaryStep } from "./SummaryStep";
import { FLEX_IZ_NO_IGNORE_ZONES_OPTION } from "./flexIzPresetSelectConstants";
import { FlexPreset } from "./types";
import { findPresetConflicts } from "./flexPresetConflicts";

/** Must match `SchedulesTabs` Tab `value`s ("one" | "two" | "three"). */
const scheduleTabValueForIndex = (index: number): "one" | "two" | "three" => {
  if (index <= 0) {
    return "one";
  }
  if (index === 1) {
    return "two";
  }
  return "three";
};

export const FlexIgnoreZonesOnboardingDialog = ({
  onClose,
  onSuccessfulSubmit,
  isFleetSite = false,
}: {
  onClose: () => void;
  onSuccessfulSubmit?: () => void;
  /** ENG-1927: fleet sites block cross-schedule preset conflicts before submit. */
  isFleetSite?: boolean;
}) => {
  const {
    isOpen,
    step,
    schedules,
    cameras,
    activeScheduleIndex,
    selectedPresetByScheduleCamera,
    cameraContexts,
    presetsByCamera,
    presetNamesByCamera,
    submitLoading,
    submitError,
    closeDialog,
    setStep,
    setActiveScheduleIndex,
    setSelectedPresetForScheduleCamera,
    loadCameraContext,
    createCustomPresetDraft,
    submitAll,
  } = useFlexIgnoreZonesOnboardingStore();

  const {
    scheduleGridEditable,
    setScheduleGridEditable,
    setSchedulesIndex,
    setClickedSchedulesTab,
    siteSchedules,
    setGrid,
    setTimeRanges,
    setDuskToDawn,
    setDuskToDawnLocation,
  } = useSiteAboutStore();

  const currentSchedule = schedules[activeScheduleIndex];
  const [newPresetDialog, setNewPresetDialog] = React.useState<{
    scheduleId: number;
    cameraId: number;
    presetLocalId: string;
    cameraName: string;
  } | null>(null);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }
    setScheduleGridEditable(false);
    return () => {
      setScheduleGridEditable(scheduleGridEditable);
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen || !currentSchedule) {
      return;
    }
    setSchedulesIndex(activeScheduleIndex);
    setClickedSchedulesTab(scheduleTabValueForIndex(activeScheduleIndex));
    localStorage.setItem("schedulesIndex", String(activeScheduleIndex));
    const selectedSchedule = siteSchedules[activeScheduleIndex];
    if (!selectedSchedule?.schedule) {
      return;
    }
    setTimeRanges(handleCreateInitialTimeSchedules(selectedSchedule.schedule));
    setGrid(handleConvertSchedulesToBooleanArrays(selectedSchedule.schedule));
    setDuskToDawn(handleCreateDuskToDawn(selectedSchedule.schedule));
    setDuskToDawnLocation(
      handleCreateDuskToDawnLocationPlusBuffer(selectedSchedule.schedule)
    );
  }, [isOpen, activeScheduleIndex, currentSchedule]);

  React.useEffect(() => {
    if (!isOpen || !currentSchedule || step !== "scheduleSelection") {
      return;
    }
    cameras.forEach((camera) => {
      loadCameraContext(camera.id);
    });
  }, [isOpen, step, currentSchedule?.id, cameras]);

  // ENG-1927: submit writes one camera-schedule-preset row at a time, so a
  // server refusal part-way would leave the site half-configured. Each
  // distinct selection becomes its own new preset on submit ("local" identity).
  const presetConflicts = React.useMemo(() => {
    if (!isFleetSite) {
      return [];
    }
    const timesById = new Map<number, any>(
      (siteSchedules || []).map((schedule: any) => [
        Number(schedule?.id),
        schedule,
      ])
    );
    return findPresetConflicts({
      schedules: schedules.map((schedule) => ({
        ...timesById.get(schedule.id),
        id: schedule.id,
        display_name: schedule.display_name,
      })),
      cameras,
      selectedPresetByScheduleCamera,
      presetsByCamera,
      presetNamesByCamera,
      identity: "local",
    });
  }, [
    isFleetSite,
    siteSchedules,
    schedules,
    cameras,
    selectedPresetByScheduleCamera,
    presetsByCamera,
    presetNamesByCamera,
  ]);

  const handleCloseDialog = () => {
    closeDialog();
    onClose();
  };

  const handleNext = async () => {
    if (!currentSchedule && step !== "summary") {
      return;
    }
    if (step === "scheduleSelection" && currentSchedule) {
      const missingSelection = cameras.find((camera) => {
        const selectedLocalId =
          selectedPresetByScheduleCamera[`${currentSchedule.id}:${camera.id}`];
        return !selectedLocalId;
      });
      if (missingSelection) {
        alert(`Choose an ignore zones preset for ${missingSelection.camera_name}.`);
        return;
      }
      const unnamedSelection = cameras.find((camera) => {
        const selectedLocalId =
          selectedPresetByScheduleCamera[`${currentSchedule.id}:${camera.id}`];
        if (!selectedLocalId || selectedLocalId === FLEX_IZ_NO_IGNORE_ZONES_OPTION) {
          return false;
        }
        const selectedPreset = (presetsByCamera[camera.id] || []).find(
          (preset) => preset.localId === selectedLocalId
        );
        return !selectedPreset?.name?.trim();
      });
      if (unnamedSelection) {
        alert(`Preset name is required for ${unnamedSelection.camera_name}.`);
        return;
      }
      if (activeScheduleIndex < schedules.length - 1) {
        setActiveScheduleIndex(activeScheduleIndex + 1);
        return;
      }
      setStep("summary");
      return;
    }
    if (step === "summary") {
      if (presetConflicts.length > 0) {
        return;
      }
      const success = await submitAll();
      if (success) {
        onSuccessfulSubmit?.();
        onClose();
      }
    }
  };

  const handleBack = () => {
    if (step === "scheduleSelection") {
      if (activeScheduleIndex === 0) {
        handleCloseDialog();
        return;
      }
      setActiveScheduleIndex(activeScheduleIndex - 1);
      return;
    }
    if (step === "summary") {
      setActiveScheduleIndex(Math.max(schedules.length - 1, 0));
      setStep("scheduleSelection");
    }
  };

  const assignments = React.useMemo(() => {
    return Object.entries(selectedPresetByScheduleCamera)
      .map(([key, presetLocalId]) => {
        const [scheduleId, cameraId] = key.split(":").map(Number);
        const schedule = schedules.find((item) => item.id === scheduleId);
        const camera = cameras.find((item) => item.id === cameraId);
        const cameraContext = cameraContexts[cameraId];
        const preset = (presetsByCamera[cameraId] || []).find(
          (item) => item.localId === presetLocalId
        );
        if (!schedule || !camera || !preset) {
          return null;
        }
        return { schedule, camera, preset, cameraContext };
      })
      .filter(Boolean) as Array<{
      schedule: (typeof schedules)[number];
      camera: (typeof cameras)[number];
      preset: FlexPreset;
      cameraContext: (typeof cameraContexts)[number] | undefined;
    }>;
  }, [selectedPresetByScheduleCamera, schedules, cameras, presetsByCamera, cameraContexts]);

  return (
    <>
      <BackdropLoadingScreen
        openState={submitLoading}
        message="Submitting Flex Ignore Zones configuration..."
        stackAboveModal
      />
      <Dialog
        open={isOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            fontFamily: "Mulish",
            minHeight: "85vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Mulish",
            fontWeight: 700,
            bgcolor: "#1B2A4A",
            color: "white",
            py: 2.25,
            pr: 6,
          }}
        >
          <div className="flex items-center gap-[8px]">
            <p className="font-mulish text-[20px] font-black text-white">
              Flex Ignore Zones Onboarding
            </p>
            <Tooltip
              title="Only the final submit saves presets, zones, and schedule assignments."
              sx={{ fontSize: "14px", fontFamily: "Mulish" }}
            >
              <InfoOutlinedIcon fontSize="small" sx={{ color: "white" }} />
            </Tooltip>
          </div>
          <IconButton
            onClick={handleCloseDialog}
            aria-label="close flex ignore zones onboarding dialog"
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.12)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {step === "scheduleSelection" && currentSchedule && (
            <div>
              <p className="font-mulish text-[20px] font-black pb-[10px]">
                {currentSchedule.display_name || "Schedule"}
              </p>
              <div className="pointer-events-none opacity-80 border border-gray-200 rounded-[6px] p-[8px]">
                <ScheduleGrid />
              </div>
              <p className="font-mulish text-[14px] pt-[12px] pb-[10px]">
                Select an ignore zones preset for each camera in this schedule.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-[12px] justify-items-start">
                {cameras.map((camera) => {
                  const cameraPresets = presetsByCamera[camera.id] || [];
                  const selectedPresetLocalId =
                    selectedPresetByScheduleCamera[`${currentSchedule.id}:${camera.id}`] ||
                    `default-${camera.id}`;
                  const selectablePresets = collapseNoIgnoreZonesPresetOptions(
                    cameraPresets,
                    selectedPresetLocalId
                  );
                  const selectedPreset =
                    selectablePresets.find(
                      (preset) => preset.localId === selectedPresetLocalId
                    ) ||
                    selectablePresets.find(
                      (preset) => preset.localId === `default-${camera.id}`
                    ) ||
                    selectablePresets[0] || {
                      localId: FLEX_IZ_NO_IGNORE_ZONES_OPTION,
                      cameraId: camera.id,
                      name: "No Ignore Zones",
                      zones: [],
                      persistedId: null,
                      source: "custom",
                      userNamed: true,
                    };

                  return (
                    <FlexIgnoreZonesEditPresetCard
                      key={`${currentSchedule.id}-${camera.id}`}
                      scheduleId={currentSchedule.id}
                      camera={camera}
                      preset={selectedPreset}
                      cameraContext={cameraContexts[camera.id]}
                      selectablePresets={selectablePresets}
                      selectedPresetLocalId={selectedPresetLocalId}
                      onSelectPreset={(presetLocalId) => {
                        setSelectedPresetForScheduleCamera(
                          currentSchedule.id,
                          camera.id,
                          presetLocalId
                        );
                      }}
                      onCreateNewPreset={() => {
                        const ctxZones = cameraContexts[camera.id]?.defaultZones;
                        const zones =
                          ctxZones && ctxZones.length > 0
                            ? ctxZones
                            : addIgnoreLabel(camera.ignore_zones || []);
                        const localId = createCustomPresetDraft(camera.id, zones);
                        setSelectedPresetForScheduleCamera(
                          currentSchedule.id,
                          camera.id,
                          localId
                        );
                        setNewPresetDialog({
                          scheduleId: currentSchedule.id,
                          cameraId: camera.id,
                          presetLocalId: localId,
                          cameraName: camera.camera_name,
                        });
                      }}
                      onPreviewImageError={() => {
                        loadCameraContext(camera.id, true);
                      }}
                    />
                  );
                })}
              </div>
            </div>
          )}
          {step === "summary" && (
            <SummaryStep assignments={assignments} conflicts={presetConflicts} />
          )}
          {submitError && (
            <div className="pt-[12px]">
              <p className="text-red-600 font-mulish text-[14px] whitespace-pre-line">
                {submitError}
              </p>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <OrangeTextButton onClick={handleBack} text="Back" disabled={submitLoading} />
          <OrangeTextButton
            onClick={handleNext}
            text={step === "summary" ? "Submit" : "Next"}
            disabled={
              submitLoading || (step === "summary" && presetConflicts.length > 0)
            }
          />
        </DialogActions>
      </Dialog>
      <FlexIgnoreZonesNewPresetDialog
        open={newPresetDialog != null}
        scheduleId={newPresetDialog?.scheduleId ?? 0}
        cameraId={newPresetDialog?.cameraId ?? 0}
        presetLocalId={newPresetDialog?.presetLocalId ?? null}
        cameraName={newPresetDialog?.cameraName ?? ""}
        onClose={(reason) => {
          if (reason === "submitted" && newPresetDialog) {
            loadCameraContext(newPresetDialog.cameraId, true);
          }
          setNewPresetDialog(null);
        }}
      />
    </>
  );
};

